import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { languageMap } from "@/i18n";
import { resolveProductKey } from "@/locales";
import { trackEvent } from "@/utils/analytics";

const SessionConflictDialog = () => {
  const pendingSessionConflict = useBookingFlowStore(
    (state) => state.pendingSessionConflict,
  );
  const resolveSessionConflict = useBookingFlowStore(
    (state) => state.resolveSessionConflict,
  );
  const language = useBookingFlowStore((state) => state.language);
  const products = useBookingFlowStore((state) => state._products);
  const conflictSessionProducts = useBookingFlowStore(
    (state) => state._conflictSessionProducts,
  );
  const conflictSessionAddOns = useBookingFlowStore(
    (state) => state._conflictSessionAddOns,
  );
  const conflictUrlProducts = useBookingFlowStore(
    (state) => state._conflictUrlProducts,
  );
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const hasTrackedShow = useRef(false);
  const isEmptyCart = conflictSessionProducts.length === 0;

  // Track when dialog is shown
  useEffect(() => {
    if (pendingSessionConflict && !hasTrackedShow.current) {
      hasTrackedShow.current = true;
      trackEvent({
        event: "session_conflict_shown",
        props: {
          type:
            conflictSessionProducts.length === 0
              ? "new_selection"
              : "selection_override",
          session_products: conflictSessionProducts,
          url_products: conflictUrlProducts,
        },
      });
    }
    if (!pendingSessionConflict) {
      hasTrackedShow.current = false;
    }
  }, [pendingSessionConflict, conflictSessionProducts, conflictUrlProducts]);

  const handleResolve = (choice: "session" | "url" | "startOver") => {
    trackEvent({
      event: "session_conflict_resolved",
      props: {
        choice,
        session_products: conflictSessionProducts,
        url_products: conflictUrlProducts,
      },
    });
    resolveSessionConflict(choice);
  };

  const getProduct = (id: string) => products.find((p) => p.id === id);

  const { sessionItems, sessionAddOnItems, urlPackageItems, urlAddOnItems } =
    useMemo(() => {
      const urlMapped = conflictUrlProducts.map((id) => ({
        id,
        product: getProduct(id),
      }));
      return {
        sessionItems: conflictSessionProducts.map((id) => getProduct(id)),
        sessionAddOnItems: conflictSessionAddOns.map((id) => getProduct(id)),
        urlPackageItems: urlMapped.filter(
          (item) => !item.product || item.product.product_type === "package",
        ),
        urlAddOnItems: urlMapped.filter(
          (item) => item.product && item.product.product_type !== "package",
        ),
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      conflictSessionProducts,
      conflictSessionAddOns,
      conflictUrlProducts,
      products,
    ]);

  const renderProductItem = (
    product: ReturnType<typeof getProduct>,
    id: string,
  ) => (
    <div key={id} className="flex items-center gap-3 py-1.5">
      <div className="w-10 h-10 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
        {product?.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={resolveProductKey(language, product.name)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-6 h-6 bg-gray-400 rounded opacity-60" />
        )}
      </div>
      <p className="flex-1 min-w-0 font-medium text-gray-900 text-sm break-words">
        {product ? resolveProductKey(language, product.name) : id}
      </p>
    </div>
  );

  return (
    <Dialog
      open={pendingSessionConflict}
      onOpenChange={() => handleResolve("session")}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden rounded-2xl sm:rounded-2xl p-5 sm:p-6">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-medium text-gray-900">
            {isEmptyCart
              ? t("sessionConflict.titleEmpty")
              : t("sessionConflict.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            {isEmptyCart
              ? t("sessionConflict.descriptionEmpty")
              : t("sessionConflict.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 sm:space-y-3 mt-2 sm:mt-3">
          {sessionItems.length > 0 && (
            <div className="bg-gray-100 rounded-2xl px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("sessionConflict.currentCart")}
              </p>
              <div className="space-y-0.5">
                {sessionItems.map((product, i) =>
                  renderProductItem(product, conflictSessionProducts[i]),
                )}
                {sessionAddOnItems.length > 0 && (
                  <>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mt-2 mb-0.5">
                      {t("sessionConflict.addOns")}
                    </p>
                    {sessionAddOnItems.map((product, i) =>
                      renderProductItem(product, conflictSessionAddOns[i]),
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {(urlPackageItems.length > 0 || urlAddOnItems.length > 0) && (
            <div className="bg-gray-100 rounded-2xl px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {isEmptyCart
                  ? t("sessionConflict.yourSelection")
                  : t("sessionConflict.newSelection")}
              </p>
              <div className="space-y-0.5">
                {urlPackageItems.map((item) =>
                  renderProductItem(item.product, item.id),
                )}
                {urlAddOnItems.length > 0 && (
                  <>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mt-2 mb-0.5">
                      {t("sessionConflict.addOns")}
                    </p>
                    {urlAddOnItems.map((item) =>
                      renderProductItem(item.product, item.id),
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-3 sm:mt-4">
          <Button
            onClick={() => handleResolve("url")}
            className="w-full rounded-full normal-case bg-gradient-to-br from-[#4C2652] to-[#5C4279] hover:shadow-md text-white focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid="session-conflict-replace"
          >
            {isEmptyCart
              ? t("sessionConflict.useSelection")
              : t("sessionConflict.useNew")}
          </Button>
          {!isEmptyCart && (
            <Button
              variant="outline"
              onClick={() => handleResolve("session")}
              className="w-full rounded-full normal-case"
              data-testid="session-conflict-keep"
            >
              {t("sessionConflict.keepExisting")}
            </Button>
          )}
          <button
            onClick={() => handleResolve("startOver")}
            className="text-sm normal-case text-gray-500 hover:text-gray-700 underline underline-offset-2 mt-1 mx-auto"
            data-testid="session-conflict-start-over"
          >
            {t("sessionConflict.startOver")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionConflictDialog;
